import dayjs from 'dayjs'
import { DEFAULT_IMAGE } from 'src/assets/images'
import {
  CompanyData,
  FirstSettingProfile,
  LoginUser,
  LoginUserProfile,
  ROLE,
  Responsibilities,
  User,
} from 'src/models/User'
import {
  MasterDataStore,
  fromMasterData,
  fromMasterDataMultiple,
  fromMasterDataWithCategoryMultiple,
  fromMasterDataWithEnumKey,
  fromMasterDataWithRegionalDivisionType,
} from '../masterData'
import {
  CompanyUpdateRequest,
  CurrentCompany,
  DecodedEmailSettings,
  SettlerUpdateRequest,
} from '../types/currentCompany'
import { TargetCompany } from './../types/targetCompany.d'

export const toLoginUser = (
  data: CurrentCompany | undefined,
  masterData: MasterDataStore | undefined,
): LoginUser | undefined => {
  const { currentCompany } = data || {}
  if (!currentCompany || !masterData) return undefined
  return {
    id: currentCompany.id,
    loggedIn: currentCompany.id !== 0,
    plan: {
      free: !!currentCompany.freePlan,
      paid: !!currentCompany.paidPlan,
      salesDemo: !!currentCompany.salesDemoPlan,
      freeAndPartner: !!currentCompany.freeAndPartner,
      role: currentCompany.role,
      isReferrer: !!currentCompany.isReferrer,
      freeTrialEndAt: currentCompany.freeTrialEndAt,
      isAdminRole: !!currentCompany.isAdminRole,
    },
    settings: {
      facebookLogin: !!currentCompany.isFacebookLogin,
      showMessageAttention: currentCompany.showMessageAttention,
    },
    showAppointmentModal: currentCompany.showAppointmentModal,
    showFeedbackModal: currentCompany.showFeedbackModal,
    profile: {
      name: currentCompany.adminName,
      nameRuby: currentCompany.adminNameFurigana,
      birthYear: currentCompany.adminAge
        ? `${dayjs(currentCompany.adminAge).year()}`
        : undefined,
      email: currentCompany.email,
      emailCc: currentCompany.ccEmailAddress?.split('|:|'),
      fbUrl: currentCompany.fbUrl,
      phoneNumber: currentCompany.phoneNumber,
      position: fromMasterData(currentCompany.position, masterData.positions),
      iconSrc: currentCompany.image?.url || DEFAULT_IMAGE,
    },
    company: {
      name: currentCompany.name,
      nameRuby: currentCompany.nameFurigana,
      // typeOfBusinessIdsを配列として取得（複数選択対応）
      businessType:
        currentCompany.typeOfBusinessIds &&
        currentCompany.typeOfBusinessIds.length > 0
          ? fromMasterDataWithCategoryMultiple(
              currentCompany.typeOfBusinessIds.map((id) => `${id}`),
              masterData.businessTypes,
            )
          : undefined,
      capital: fromMasterData(currentCompany.capital, masterData.capitals),
      numberOfEmployees: fromMasterDataWithEnumKey(
        currentCompany.employeeNumber,
        masterData.numberOfEmployees,
      ),
      prefecture: fromMasterDataWithRegionalDivisionType(
        currentCompany.prefectureId,
        masterData.prefectures,
      ),
      foundedOn: currentCompany.foundedAt
        ? `${dayjs(currentCompany.foundedAt).year()}`
        : undefined,
      old: currentCompany.foundedAt
        ? dayjs().year() - dayjs(currentCompany.foundedAt).year()
        : undefined,
      isListed: currentCompany.isListedCompany,
      serviceDescription: currentCompany.serviceDescription,
      businessObject: fromMasterDataWithEnumKey(
        currentCompany.businessObject,
        masterData.businessObjects,
      ),
      hpUrl: currentCompany.hpUrl,
      fbUrl: currentCompany.fbUrl,
      scheduleUrl: currentCompany.scheduleUrl,
      isActiveScheduleUrl: currentCompany.isActiveScheduleUrl,
      scheduleUrlBannerLastClosedAt:
        currentCompany.scheduleUrlBannerLastClosedAt,
      canCreatePersona: currentCompany.canCreatePersona,
      paidPlan: currentCompany.paidPlan,
      createStatus: currentCompany.createStatus,
    },
    responsibilities: {
      domains: fromMasterDataMultiple(
        currentCompany.companyDecidableDivisions?.map(
          ({ decidableDivisionId }) => decidableDivisionId,
        ),
        masterData.resposibleDomains,
      ),
      features: fromMasterDataMultiple(
        currentCompany.companyFeatures?.map(({ featureId }) => featureId),
        masterData.features.filter(
          (feature) => feature.category !== 'connection', // 「つながり」は除外する
        ),
      ),
      onlineMtgAvailable: currentCompany.onlineMeetingAvailable,
      message: currentCompany.messageFromAdmin,
    },
    notice: {
      chatworkNoticeFlag: currentCompany.chatworkNoticeFlag,
      chatworkRoomId: currentCompany.chatworkRoomId,
      chatworkApiToken: currentCompany.chatworkApiToken,
      slackNoticeFlag: currentCompany.slackNoticeFlag,
      slackApiToken: currentCompany.slackApiToken,
      emailNoticeFlag: currentCompany.emailNoticeFlag,
      smsNoticeFlag: currentCompany.smsNoticeFlag,
      messageAvailable: currentCompany.messageAvailable,
      receiveAllEmailSetting: decodeEmailSettings(
        currentCompany.receiveAllEmailSetting,
        currentCompany.slackNoticeFlag,
        currentCompany.chatworkNoticeFlag,
      ),
    },
    needs: {
      items: fromMasterDataWithCategoryMultiple(
        currentCompany.companyBuyNeedOptions?.map(({ id }) => id),
        masterData.needs,
      ),
    },
    solutions: {
      items: fromMasterDataWithCategoryMultiple(
        currentCompany.solutions?.map(({ id }) => id),
        masterData.solutions,
      ),
    },
    personas: {
      items: currentCompany.personas.map((persona) => ({
        needs: fromMasterDataMultiple(
          persona.personaCompanyBuyNeedOptionIds,
          masterData.needs,
        ),
        numbersOfEnployees: fromMasterDataMultiple(
          persona.personaEmployeeNumberIds,
          masterData.numberOfEmployees,
        ),
      })),
    },
    specialties: currentCompany.specialties,
    member: currentCompany.member,
    isAgreedPolicy: currentCompany.isAgreedPolicy,
    withInSalesDemoPlanPeriod: currentCompany.withInSalesDemoPlanPeriod,
    salesDemoDaysLeftText: currentCompany.salesDemoDaysLeftText,
  }
}
export const toUser = (data: TargetCompany): User => ({
  id: data.id,
  profile: {
    name: data.adminName || '',
    fbUrl: data.fbUrl || '',
    position: data.position,
    iconSrc:
      typeof data.imageUrl === 'string'
        ? data.imageUrl
        : data.imageUrl?.medium?.url || data.imageUrl?.url || DEFAULT_IMAGE,
    isCertified: data.isCertified,
  },
  company: {
    name: data.name || '',
    businessType: Array.isArray(data.typeOfBusiness)
      ? data.typeOfBusiness.join('、')
      : data.typeOfBusiness
      ? data.typeOfBusiness
      : null, // typeOfBusinessは文字列配列のため、結合して文字列に変換
    capital: data.capitalText,
    numberOfEmployees: data.employeeNumberText,
    prefecture: data.prefecture,
    foundedOn: data.foundedAt ? `${dayjs(data.foundedAt).year()}` : undefined,
    old: data.foundedAt
      ? dayjs().year() - dayjs(data.foundedAt).year()
      : undefined,
    isListed: data.isListedCompany,
    serviceDescription: data.serviceDescription || '',
    businessObject: data.businessObject,
    storyUrl: data.storyUrl || '',
    hpUrl: data.hpUrl || '',
    fbUrl: data.fbUrl || '',
    canCreatePersona: data.canCreatePersona,
    paidPlan: data.paidPlan,
    roomUrl: data.roomUrl,
    messageAvailable: data.messageAvailable,
    adminAges: data.adminAges,
    matchingStatus: data.matchingStatus,
    isMatch: data.isMatch,
    feedbackCount: data.feedbackCount,
    feedbackScore: data.feedbackScore,
    connectionCount: data.connectionCount,
    connectionCompanies: data.connectionCompanies?.map((c) => ({
      id: c.id,
      name: c.name,
      adminName: c.adminName,
      imageUrl:
        typeof c.imageUrl === 'string'
          ? c.imageUrl
          : c.imageUrl?.thumb?.url || c.imageUrl?.url || '',
      matchingStatus: c.matchingStatus,
    })),
    introductoryTextCompanies: data.introductoryTextCompanies?.map((c) => ({
      id: c.id,
      name: c.name,
      adminName: c.adminName,
      imageUrl:
        typeof c.imageUrl === 'string'
          ? c.imageUrl
          : c.imageUrl?.thumb?.url || c.imageUrl?.url || '',
      content: c.content,
      createdAt: dayjs(c.createdAt),
    })),
  },
  needs: {
    items: data.companyBuyNeedsText,
  },
  solutions: {
    items: data.companySolutionsText,
  },
  personas: {
    items: [
      {
        needs: data.recommendCompanyConditionTargets,
        numbersOfEnployees: data.recommendCompanyConditionEmployee,
      },
    ],
  },
  responsibilities: {
    domains: data.companyDecidableDivisions,
    features: data.companyFeatures,
    message: data.messageFromAdmin,
  },
  specialties: {
    items: data.specialties.map((specialty) => specialty.name),
  },
  kept: data.kept,
  isReferrer: data.role === ROLE.ONLYSTORY_REFERRER,
})
export const toProfileFormData = async (user: LoginUserProfile) => {
  const formdata = new FormData()
  formdata.append(`company[admin_name]`, user.name)
  formdata.append(`company[admin_name_furigana]`, user.nameRuby || '')
  formdata.append(`company[email]`, user.email)

  user.emailCc?.forEach((value) =>
    formdata.append(`company[company_email_cc][]`, value),
  )

  formdata.append(`company[fb_url]`, user.fbUrl || '')
  formdata.append(`company[phone_number]`, user.phoneNumber || '')
  if (user.birthYear)
    formdata.append(`company[admin_age]`, `${user.birthYear}-1-1`)
  formdata.append(`company[position]`, user.position?.value || '')
  if (user.iconSrc?.startsWith('blob'))
    formdata.append(`company[image]`, await (await fetch(user.iconSrc)).blob())
  return formdata
}

export const toCompanyUpdateRequest = (
  company: CompanyData,
): Partial<CompanyUpdateRequest> => {
  // businessTypeが配列の場合と単一の場合の両方に対応
  const businessTypeIds = Array.isArray(company.businessType)
    ? company.businessType.map((item) => item.value)
    : company.businessType?.value
    ? [company.businessType.value]
    : undefined

  return {
    name: company.name,
    name_furigana: company.nameRuby,
    ...(businessTypeIds && businessTypeIds.length > 0
      ? {
          type_of_business_ids: businessTypeIds,
        }
      : {}),
    business_object: company.businessObject?.value,
    employee_number: company.numberOfEmployees?.value,
    founded_at: `${company.foundedOn}-1-1`,
    prefecture_id: company.prefecture?.value,
    hp_url: company.hpUrl,
    schedule_url: company.scheduleUrl,
    capital: company.capital?.value,
    is_listed_company: company.isListed,
    service_description: company.serviceDescription,
  }
}

export const toSettlerUpdateRequest = ({
  domains = [],
  features = [],
  onlineMtgAvailable,
  message,
}: Responsibilities): Partial<SettlerUpdateRequest> => ({
  company: {
    company_decidable_division_ids: domains.length
      ? domains.map(({ value }) => value)
      : undefined,
    feature_ids: features.map(({ value }) => value),
    online_meeting_available:
      onlineMtgAvailable != null ? onlineMtgAvailable : null,
    message_from_admin: message,
  },
})

export const toCompanyFirstSetting = async (
  company: FirstSettingProfile,
  needsId: string[],
  solutionsId: string[],
) => {
  const formdata = new FormData()
  formdata.append(`company[admin_name]`, company.adminName)
  formdata.append(`company[email]`, company.email)
  formdata.append(`company[position]`, company.position?.value || '')
  formdata.append(`company[admin_age]`, `${company.birthYear}-1-1`)
  if (company.image?.startsWith('blob'))
    formdata.append(`company[image]`, await (await fetch(company.image)).blob())

  formdata.append(`company[name]`, company.name)
  const businessTypeIds = Array.isArray(company.businessType)
    ? company.businessType.map((item) => item.value)
    : company.businessType?.value
    ? [company.businessType.value]
    : []
  businessTypeIds.forEach((id) => {
    formdata.append('company[type_of_business_ids][]', id)
  })
  formdata.append(
    `company[business_object]`,
    company.businessObject?.value || '',
  )
  formdata.append(
    `company[employee_number]`,
    company.numberOfEmployees?.value || '',
  )
  formdata.append(`company[founded_at]`, `${company.foundedOn}-1-1`)
  formdata.append(`company[prefecture_id]`, company.prefecture?.value || '')
  formdata.append(`company[hp_url]`, company.hpUrl)
  formdata.append(`company[service_description]`, company.serviceDescription)
  formdata.append(`company[prefecture_id]`, company.prefecture?.value || '')

  needsId.forEach((id) => {
    formdata.append('company[needs_ids][]', id)
  })

  solutionsId.forEach((id) => {
    formdata.append('company[solutions_ids][]', id)
  })

  return formdata
}

export const decodeEmailSettings = (
  json: string,
  enableSlack: boolean,
  enableChatwork: boolean,
): DecodedEmailSettings => {
  const data = JSON.parse(json)
  const isReceiveAllOnly = json === JSON.stringify({ is_receive_all: '1' })

  return {
    is_receive_all: data.is_receive_all == '1',
    is_receive_chat_email:
      data.is_receive_chat_email === '1' || isReceiveAllOnly,
    is_receive_chat_email_slack:
      data.is_receive_chat_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_receive_chat_email_chatwork:
      data.is_receive_chat_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_not_read_message_count_email:
      data.is_not_read_message_count_email === '1' || isReceiveAllOnly,
    is_not_read_message_count_email_slack:
      data.is_not_read_message_count_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_not_read_message_count_email_chatwork:
      data.is_not_read_message_count_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_receive_request_email:
      data.is_receive_request_email === '1' || isReceiveAllOnly,
    is_receive_request_email_slack:
      data.is_receive_request_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_receive_request_email_chatwork:
      data.is_receive_request_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_receive_matching_email:
      data.is_receive_matching_email === '1' || isReceiveAllOnly,
    is_receive_matching_email_slack:
      data.is_receive_matching_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_receive_matching_email_chatwork:
      data.is_receive_matching_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_request_count_email:
      data.is_request_count_email === '1' || isReceiveAllOnly,
    is_request_count_email_slack:
      data.is_request_count_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_request_count_email_chatwork:
      data.is_request_count_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_contribution_keyword_email:
      data.is_contribution_keyword_email === '1' || isReceiveAllOnly,
    is_contribution_keyword_email_slack:
      data.is_contribution_keyword_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_contribution_keyword_email_chatwork:
      data.is_contribution_keyword_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_purchase_notify_email:
      data.is_purchase_notify_email === '1' || isReceiveAllOnly,
    is_purchase_notify_email_slack:
      data.is_purchase_notify_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_purchase_notify_email_chatwork:
      data.is_purchase_notify_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
    is_contribution_specialty_email:
      data.is_contribution_specialty_email === '1' || isReceiveAllOnly,
    is_contribution_specialty_email_slack:
      data.is_contribution_specialty_email_slack === '1' ||
      (isReceiveAllOnly && enableSlack),
    is_contribution_specialty_email_chatwork:
      data.is_contribution_specialty_email_chatwork === '1' ||
      (isReceiveAllOnly && enableChatwork),
  }
}

export const encodeEmailSettings = (data: DecodedEmailSettings) => {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(data).map(([k, v]) => {
        return [k, v ? '1' : '0']
      }),
    ),
  )
}
